import { loadConfig } from './config';
import { clearAuth, getAuth } from '@/shared/auth/storage';

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

interface CallOptions {
  /** /api/pos/* path-д кассирын token (cookie + X-Ts-Terminal-Activation) ашиглана. */
  posScope?: boolean;
  /** Cashier override (POS-д ашиглана). */
  cashierToken?: string | null;
  /** AbortSignal — TanStack Query cancellation-руу холбоно. */
  signal?: AbortSignal;
}

let onUnauthorized: (() => void) | null = null;

/**
 * 401 ирэхэд auth localStorage-ыг арилгаж logout/login redirect хийнэ.
 * AuthProvider-аас бүртгэгдэнэ.
 */
export function setUnauthorizedHandler(fn: () => void): void {
  onUnauthorized = fn;
}

/**
 * Active tenant/company scope holder.
 * URL-аас унших `TenantScopeProvider`-аар `setApiActiveScope({...})` болж бичигдэнэ.
 * `null` (cold-start, public page) үед `X-Ts-Tenant`/`X-Ts-Company` header-ийг
 * localStorage-ийн "last-visited" cache-аас (`tsetsu.tenantId/companyId`) уншиж
 * fallback хийнэ. Phase 5-д энэ fallback-ыг устгана.
 */
let activeScope: { tenantId: string; companyId: string } | null = null;

export function setApiActiveScope(
  scope: { tenantId: string; companyId: string } | null,
): void {
  activeScope = scope;
}

export function getApiActiveScope(): { tenantId: string; companyId: string } | null {
  return activeScope;
}

function buildHeaders(
  hasBody: boolean,
  posScope: boolean,
  cashierToken: string | null,
): Headers {
  const headers = new Headers();
  headers.set('Accept', 'application/json');
  if (hasBody) headers.set('Content-Type', 'application/json');

  // POS scope нь зөвхөн cashier JWT эсвэл cookie-аар authenticate хийгддэг.
  // User JWT (tsetsu.token) audience='empty'-тэй учир Terminal/TerminalCashier
  // scheme-д 'invalid_token' буцаана. Тиймээс POS scope-д userToken илгээхгүй.
  const userToken = getAuth('tsetsu.token');
  const token = posScope ? cashierToken : userToken;
  if (token) headers.set('Authorization', 'Bearer ' + token);

  // URL-аас уншсан active scope нь primary; cold-start-д localStorage cache fallback.
  const tenantId = activeScope?.tenantId ?? getAuth('tsetsu.tenantId');
  if (tenantId) headers.set('X-Ts-Tenant', tenantId);

  const companyId = activeScope?.companyId ?? getAuth('tsetsu.companyId');
  if (companyId) headers.set('X-Ts-Company', companyId);

  if (posScope) headers.set('X-Ts-Terminal-Activation', '1');
  return headers;
}

async function call<T>(
  method: Method,
  path: string,
  body?: unknown,
  opts?: CallOptions,
): Promise<T> {
  const cfg = await loadConfig();
  // POS scope = cashier JWT-аар auth хийгдэх endpoint-ууд (POS terminal-аас).
  // `/api/pos/admin/*` нь admin / manager UI-аас user JWT-ээр дуудагдах тул
  // POS scope-аас exclude хийнэ (`/api/pos/admin/shifts`, `/api/pos/admin/sales`,
  // ирээдүйд нэмэгдэх admin endpoint-ууд автомат хамаарагдана).
  const posScope = opts?.posScope ??
    (path.startsWith('/api/pos/') && !path.startsWith('/api/pos/admin/'));
  const hasBody = body != null;
  const headers = buildHeaders(hasBody, posScope, opts?.cashierToken ?? null);

  const init: RequestInit = {
    method,
    headers,
    credentials: posScope ? 'include' : 'same-origin',
  };
  if (hasBody) init.body = JSON.stringify(body);
  if (opts?.signal) init.signal = opts.signal;

  const res = await fetch(cfg.apiBaseUrl + path, init);

  if (res.status === 401) {
    if (!posScope) {
      clearAuth();
      onUnauthorized?.();
    }
    throw new ApiError(401, 'Unauthorized', null);
  }

  const text = await res.text();
  let parsed: unknown = null;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }
  }

  if (!res.ok) {
    // Backend response shape:
    //  - ProblemDetails (middleware-аас)        → { detail: "..." }
    //  - Local controller catch (POS, etc.)     → { error: "..." }
    //  - Validation problem details             → { title: "...", errors: {...} }
    //  - Plain string body
    let message: string | null = null;
    if (parsed && typeof parsed === 'object') {
      const p = parsed as Record<string, unknown>;
      if (typeof p.detail === 'string' && p.detail) message = p.detail;
      else if (typeof p.error === 'string' && p.error) message = p.error;
      else if (typeof p.message === 'string' && p.message) message = p.message;

      // Validation problem details — `errors` map нь field-аар грүпплэгдсэн
      // string[]. `title`-аас илүү тодорхой message-ыг сонгох (e.g., "Items:
      // Буцаалтын мөр заавал орох ёстой."). Олон алдаа байвал эхний 3-ыг join.
      if (!message && p.errors && typeof p.errors === 'object') {
        const errs = p.errors as Record<string, unknown>;
        const lines: string[] = [];
        for (const [field, val] of Object.entries(errs)) {
          if (Array.isArray(val)) {
            for (const v of val) {
              if (typeof v === 'string' && v) lines.push(v);
            }
          } else if (typeof val === 'string' && val) {
            lines.push(val);
          }
          if (lines.length >= 3) break;
          // field нэрийг message-руу хавсаргахгүй (backend-ийн text аль хэдийн
          // ойлгомжтой, "Items" англи нэрийг хэрэглэгчид харуулах нь муу UX).
          void field;
        }
        if (lines.length > 0) message = lines.join(' · ');
      }

      if (!message && typeof p.title === 'string' && p.title) message = p.title;
    } else if (typeof parsed === 'string' && parsed) {
      message = parsed;
    }
    throw new ApiError(res.status, message ?? `HTTP ${res.status}`, parsed);
  }
  return parsed as T;
}

export const api = {
  get: <T>(path: string, opts?: CallOptions) => call<T>('GET', path, undefined, opts),
  post: <T>(path: string, body?: unknown, opts?: CallOptions) =>
    call<T>('POST', path, body, opts),
  put: <T>(path: string, body?: unknown, opts?: CallOptions) => call<T>('PUT', path, body, opts),
  patch: <T>(path: string, body?: unknown, opts?: CallOptions) =>
    call<T>('PATCH', path, body, opts),
  del: <T>(path: string, opts?: CallOptions) => call<T>('DELETE', path, undefined, opts),
};
